/**
 * THE TENANT'S TIME ZONE — `companies.time_zone`, and nothing else.
 *
 * Standing rule (Aug 2026): the zone a tenant picks in Settings → Regional &
 * Tax, served by GET /company/locale, is the ONLY source of that tenant's
 * business dates. Not 'America/Edmonton' because that is where most tenants
 * are, and not the browser's or the phone's own zone because that is where the
 * reader happens to be sitting. Lone Star and Apex Energy run on Chicago time;
 * an Edmonton default printed their evening jobs on the wrong day, and a
 * dispatcher in Alberta looking at a Texas board saw Alberta's "today".
 *
 * ── THE THREE STATES, AND WHY THERE IS NO FOURTH ────────────────────────────
 *
 *   loading  the server has not answered and this device holds no copy
 *   ready    a zone the SERVER gave — live, or this device's copy of its last
 *            answer for THIS company (`source` says which)
 *   error    the server could not be reached and this device has never held
 *            this company's zone; or it answered with no usable zone
 *
 * There is no "fall back to something plausible" state. A guessed zone renders
 * a date that looks right and is a day out for half the evening, which is
 * worse than no date — the em-dash rule, one layer up.
 *
 * ── THE DEVICE COPY (offline) ───────────────────────────────────────────────
 *
 * A field hand opening the app in a dead zone cannot reach /company/locale,
 * and React Query PAUSES an online-mode query while the browser is offline —
 * it never errors, it sits in `pending` forever. Without a device copy that
 * hand would have no dates at all. So every successful answer is written here,
 * keyed by company id, and used ONLY when the call cannot complete (failed,
 * refused or paused). It is the server's own last answer, not a guess, and it
 * is never consulted while a live call is still in flight — so a zone changed
 * in Settings takes effect on the next load that reaches the server.
 *
 * localStorage rather than IndexedDB because it has to be readable on the
 * first render, synchronously, before a gate can decide what to show. Losing
 * it (private window, iOS evicting WebView storage) costs the offline copy and
 * nothing else: the result is the honest error state, never a wrong date.
 */

/** Is this a zone `Intl` can actually compute in? Membership is the API's job. */
export function isUsableTimeZone(zone: unknown): zone is string {
  if (typeof zone !== 'string' || zone.trim().length === 0) return false;
  try {
    new Intl.DateTimeFormat('en-CA', { timeZone: zone });
    return true;
  } catch {
    return false;
  }
}

export type TenantZoneFailure =
  /** The call failed or was refused, and this device has no copy. */
  | { status: 'error'; reason: 'unreachable' | 'refused'; error: unknown }
  /** The server answered, and its answer was not a usable zone. */
  | { status: 'error'; reason: 'missing'; error: null };

export type TenantZoneState =
  /** Signed out: there is no tenant, so there is no zone to wait for. */
  | { status: 'signed-out' }
  | { status: 'loading' }
  | { status: 'ready'; zone: string; source: 'server' | 'device' }
  | TenantZoneFailure;

const KEY_PREFIX = 'fieldiq.tenantZone.v1.';

/** This device's copy of the server's last answer for this company, or null. */
export function readCachedTenantZone(companyId: string | null | undefined): string | null {
  if (!companyId) return null;
  try {
    const raw = window.localStorage.getItem(KEY_PREFIX + companyId);
    // A malformed or retired value is discarded, never coerced into a zone.
    return isUsableTimeZone(raw) ? raw : null;
  } catch {
    return null;
  }
}

export function writeCachedTenantZone(companyId: string | null | undefined, zone: string): void {
  if (!companyId || !isUsableTimeZone(zone)) return;
  try {
    window.localStorage.setItem(KEY_PREFIX + companyId, zone);
  } catch {
    // Storage refused (quota, private mode). The live answer still renders;
    // only the offline copy is lost.
  }
}

/** True when an error is the server declining, not the network failing. */
function isRefusalError(error: unknown): boolean {
  const e = error as { response?: { status?: unknown }; status?: unknown } | null | undefined;
  const status = e?.response?.status ?? e?.status;
  return status === 401 || status === 403;
}

/**
 * The pure decision, separate from React so every branch is testable.
 *
 * `serverZone` is `undefined` when there is no server answer yet, and the
 * answer's `time_zone` (possibly null) when there is one — the two are
 * different facts, and `?? null` on the caller's side would collapse them.
 */
export function resolveTenantZone(input: {
  signedIn: boolean;
  status: 'pending' | 'success' | 'error';
  fetchStatus: 'fetching' | 'paused' | 'idle';
  serverZone: string | null | undefined;
  error: unknown;
  cached: string | null;
}): TenantZoneState {
  const { signedIn, status, fetchStatus, serverZone, error, cached } = input;
  if (!signedIn) return { status: 'signed-out' };

  if (status === 'success') {
    // The server answered. Its answer wins over the device copy, including an
    // answer of "no zone" — a stale copy is not evidence against a live reply.
    if (isUsableTimeZone(serverZone)) return { status: 'ready', zone: serverZone, source: 'server' };
    return { status: 'error', reason: 'missing', error: null };
  }

  // A pending call that is actually in flight: wait for it, even with a device
  // copy in hand, so a zone changed in Settings is what the next load shows.
  if (status === 'pending' && fetchStatus === 'fetching') return { status: 'loading' };

  // Failed, refused, paused offline, or not yet started: the device copy if
  // this device has ever loaded this company's zone, otherwise say so.
  if (cached) return { status: 'ready', zone: cached, source: 'device' };
  if (status === 'pending') {
    // Paused (offline) with nothing on the device is a failure to reach the
    // server; idle-and-pending is the instant before the first fetch starts.
    return fetchStatus === 'paused'
      ? { status: 'error', reason: 'unreachable', error: null }
      : { status: 'loading' };
  }
  return { status: 'error', reason: isRefusalError(error) ? 'refused' : 'unreachable', error };
}

/**
 * ── STALE ZONE DATA IN THE BROWSER (Alberta and BC, Nov 2026) ───────────────
 *
 * Alberta (America/Edmonton, and America/Yellowknife, which tzdata links to it)
 * stays on UTC-6 for good, and BC (America/Vancouver) stays on UTC-7 for good.
 * IANA tzdata 2026b (BC) and 2026c (Alberta) show this by treating
 * 2026-11-01 02:00 as a fall-back that never comes. A browser bundling older
 * zone data (a tablet whose Chrome is never updated, an old iOS) still falls
 * back that night. After that it shows every tenant time an hour EARLY and
 * reads a typed datetime-local an hour LATE, which puts a clock-out an hour
 * off on payroll.
 *
 * So each of the three zones is checked ONCE per page load: what offset does
 * this browser give it on 2026-12-01T12:00Z? If the answer is not the
 * permanent offset, this browser's data is stale for that zone. From that
 * zone's 2026-03-08 spring-forward (the last change either province made) the
 * zone is then swapped for the fixed-offset zone with the same clock. Before
 * that instant the real zone is kept, because old data is correct for the
 * past. The zones are checked separately because a 2026b browser is current
 * for BC and stale for Alberta.
 *
 * Every tenant-zone Intl call goes through `effectiveTimeZone`, via
 * `formatInZone`, `tenantCalendarDate` and `zonedParts` in calendar-date.ts.
 * The datetime-local write path runs on `zonedParts`. A call site that passes
 * `{ timeZone }` to Intl directly skips this check and shows the hour-early
 * time. Zones that still change clocks (Denver, Chicago) are never touched.
 *
 * Etc/GMT signs are POSIX: Etc/GMT+6 is UTC-6.
 */
const PERMANENT_ZONES: Record<string, { fixed: string; offsetMinutes: number; from: number }> = {
  // 2026-03-08 02:00 MST = 09:00Z; 02:00 PST = 10:00Z.
  'America/Edmonton': { fixed: 'Etc/GMT+6', offsetMinutes: -360, from: Date.UTC(2026, 2, 8, 9) },
  'America/Yellowknife': { fixed: 'Etc/GMT+6', offsetMinutes: -360, from: Date.UTC(2026, 2, 8, 9) },
  'America/Vancouver': { fixed: 'Etc/GMT+7', offsetMinutes: -420, from: Date.UTC(2026, 2, 8, 10) },
};

/** A winter instant after the fall-back that no longer happens. */
const STALE_PROBE = new Date(Date.UTC(2026, 11, 1, 12));

const staleByZone = new Map<string, boolean>();

/** This browser's UTC offset for `zone` at `at`, in minutes, or null if Intl cannot say. */
function browserOffsetMinutes(zone: string, at: Date): number | null {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: zone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(at);
    const get = (type: string) => Number(parts.find((p) => p.type === type)?.value);
    const wall = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour') % 24, get('minute'));
    return Number.isFinite(wall) ? Math.round((wall - at.getTime()) / 60000) : null;
  } catch {
    return null;
  }
}

/** True when this browser's zone data predates the zone's permanent offset. Checked once per zone. */
export function isZoneDataStale(zone: string): boolean {
  const rule = PERMANENT_ZONES[zone];
  if (!rule) return false;
  let stale = staleByZone.get(zone);
  if (stale === undefined) {
    const seen = browserOffsetMinutes(zone, STALE_PROBE);
    // An offset Intl cannot compute is not evidence of stale data; leave the zone alone.
    stale = seen !== null && seen !== rule.offsetMinutes;
    staleByZone.set(zone, stale);
  }
  return stale;
}

/** Tests only: forget the per-load checks so a test can swap in a different Intl. */
export function resetZoneDataProbe(): void {
  staleByZone.clear();
}

/**
 * The zone to hand Intl for an instant: the tenant's own, or its fixed-offset
 * stand-in if this browser's data is stale for it and `at` is on or after the
 * last clock change. Every zoned Intl call in the app goes through here.
 */
export function effectiveTimeZone(zone: string, at: Date): string {
  const rule = PERMANENT_ZONES[zone];
  if (!rule || !(at.getTime() >= rule.from)) return zone;
  return isZoneDataStale(zone) ? rule.fixed : zone;
}
